/**
 * cro-pdp.js — CRO product page behaviour. Everything critical already works without it
 * (radios + a real <form action="/cart/add">); this layer makes it instant and smooth.
 */
import {
  $,
  $$,
  openSheet,
  closeSheet,
  addToCart,
  isInCart,
  inCartToast,
  toast,
  openCart,
  openSearch,
  openMenu,
  copyText,
  fetchSection,
  refreshWishlist,
  replaceUrl,
  track,
} from '@theme/cro-core';

const root = $('.cro-pdp');
if (root) init(root);

function init(root) {
  const data = JSON.parse($('[data-cro-product]', root)?.textContent || '{}');
  const form = $('[data-cro-form]', root);
  const atc = $('[data-cro-atc]', root);
  const atcLabel = $('[data-cro-atc-label]', root);
  const radios = $$('[data-cro-sizes] input[type=radio]', root);
  const sizeSheet = $('[data-cro-size-sheet]', root);
  const wl = $('.cro-cta__wl .th_wl_col_btn', root);
  let busy = false;

  /* ---------------- gallery: photo 2 after load, the rest on first swipe */
  const track_ = $('[data-cro-track]', root);
  const deferred = $$('.cro-gal__img[data-src]', root);
  const hydrate = (img) => {
    if (!img || !img.dataset.src) return;
    img.srcset = img.dataset.srcset || '';
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  };
  const hydrateAll = () => deferred.forEach(hydrate);
  const afterLoad = () => setTimeout(() => hydrate(deferred[0]), 250);
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true });
  track_?.addEventListener('pointerdown', hydrateAll, { once: true, passive: true });
  track_?.addEventListener('scroll', hydrateAll, { once: true, passive: true });

  /* ---------------- gallery dots */
  const dots = $$('[data-cro-dots] i', root);
  let current = 0;
  if (track_ && dots.length > 1 && 'IntersectionObserver' in window) {
    const slides = $$('.cro-gal__slide', track_);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting && en.intersectionRatio > 0.55) {
            current = slides.indexOf(en.target);
            dots.forEach((d, i) => d.classList.toggle('is-on', i === current));
          }
        });
      },
      { root: track_, threshold: [0.6] }
    );
    slides.forEach((s) => io.observe(s));
    let swiped = false;
    track_.addEventListener(
      'scroll',
      () => {
        if (!swiped) {
          swiped = true;
          track('cro_gallery_swipe', { product_id: data.id });
        }
      },
      { passive: true, once: true }
    );
  }

  /* ---------------- full-screen photos */
  let zoom;
  /* Pinch-zooming a photo zooms the whole page, and a fixed ✕ stays where it was on the unzoomed
     page — off screen. While the viewer is open the ✕ follows the visible area (same size, top
     right); closing it zoomed in brings the page back to 1×. */
  const vv = window.visualViewport;
  const placeX = () => {
    const x = zoom && zoom.querySelector('.cro-zoom__x');
    if (!x || !vv) return;
    if (vv.scale <= 1.01) {
      x.style.cssText = '';
      return;
    }
    const s = 1 / vv.scale;
    x.style.cssText = `left:0;top:0;right:auto;transition:none;transform-origin:0 0;transform:translate(${vv.offsetLeft + vv.width - 54 * s}px,${vv.offsetTop + 12 * s}px) scale(${s})`;
  };
  const resetZoom = () => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!vv || vv.scale <= 1.01 || !meta) return;
    const orig = meta.getAttribute('content');
    meta.setAttribute('content', `${orig}, maximum-scale=1.0`);
    setTimeout(() => meta.setAttribute('content', orig), 400);
  };
  const openZoom = (index) => {
    if (!zoom) {
      zoom = document.createElement('dialog');
      zoom.className = 'cro-sheet cro-zoom';
      zoom.id = 'cro-zoom';
      zoom.setAttribute('aria-label', 'Photos');
      zoom.innerHTML =
        '<button type="button" class="cro-ib cro-zoom__x" data-cro-close aria-label="Close"><svg class="cro-i"><use href="#cro-i-close"/></svg></button><div class="cro-zoom__list"></div>';
      zoom.addEventListener('cro:open', () => {
        if (!vv) return;
        vv.addEventListener('resize', placeX);
        vv.addEventListener('scroll', placeX);
        placeX();
      });
      zoom.addEventListener('cro:close', () => {
        if (vv) {
          vv.removeEventListener('resize', placeX);
          vv.removeEventListener('scroll', placeX);
        }
        resetZoom();
        zoom.querySelector('.cro-zoom__x').style.cssText = '';
      });
      const list = zoom.querySelector('.cro-zoom__list');
      (data.zoom || []).forEach((src, i) => {
        const img = new Image();
        img.src = src;
        img.alt = `${data.title} photo ${i + 1}`;
        img.loading = i === index ? 'eager' : 'lazy';
        img.decoding = 'async';
        img.width = 1080;
        img.height = 1440;
        list.appendChild(img);
      });
      root.appendChild(zoom);
    }
    openSheet(zoom);
    const target = zoom.querySelectorAll('.cro-zoom__list img')[index];
    if (target) requestAnimationFrame(() => (zoom.scrollTop = target.offsetTop));
    track('cro_zoom_open', { product_id: data.id });
  };
  $('[data-cro-zoom]', root)?.addEventListener('click', () => openZoom(current));
  track_?.addEventListener('click', (e) => {
    if (e.target.closest('.cro-gal__slide')) openZoom(current);
  });

  /* ---------------- size guide: the interactive version (inches ⇄ cm, pick a size, add from the
     guide) is a separate file loaded the first time the guide opens, so it costs nothing until then */
  const guide = $('[data-cro-size-guide]', root);
  if (guide && guide.dataset.js) {
    let loading;
    const loadGuide = () =>
      (loading = loading || import(guide.dataset.js).then((m) => m.init(guide, root)).catch(() => {}));
    guide.addEventListener('cro:open', loadGuide);
    // start the download on the press that opens it
    $$(`[data-cro-open="${guide.id}"]`, root).forEach((b) =>
      b.addEventListener('pointerdown', loadGuide, { passive: true, once: true })
    );
  }

  /* ---------------- size selection */
  const selected = () => radios.find((r) => r.checked);
  const setVariantUrl = (id) => {
    const url = new URL(location.href);
    url.searchParams.set('variant', id);
    replaceUrl(url);
  };
  const fixedId = () => $('input[type=hidden][name=id], select[name=id]', form || root)?.value || '';
  // The CTA: "Add to cart · M", or "View cart" when that exact size is already in the cart
  // (a second one is a quantity change, which the cart handles).
  const label = () => {
    if (!data.available || !atc || !atcLabel) return;
    const r = selected();
    const id = r ? r.value : fixedId();
    const there = isInCart(id);
    atc.classList.toggle('is-incart', there);
    atcLabel.textContent = there ? 'View cart' : r ? `Add to cart · ${r.dataset.title}` : 'Add to cart';
  };
  const markSizes = () =>
    $$('.cro-size input', root).forEach((i) => i.closest('.cro-size').classList.toggle('is-incart', isInCart(i.value)));
  const onSize = (updateUrl = true) => {
    const r = selected();
    form?.classList.remove('is-err');
    if (!r) return label();
    if (updateUrl !== false) setVariantUrl(r.value);
    if (wl) wl.dataset.variant_id = r.value;
    const price = $('[data-cro-price]', root);
    if (price && r.dataset.price) price.textContent = r.dataset.price;
    label();
  };
  radios.forEach((r) => r.addEventListener('change', () => onSize()));
  $('select[name=id]', root)?.addEventListener('change', label);
  onSize(!!selected());
  document.addEventListener('cro:cart', () => {
    markSizes();
    if (!busy && !atc?.classList.contains('is-done')) label();
  });

  // Mirror the sizes into the sheet the sticky CTA opens when nothing is chosen yet.
  const sheetSizes = $('[data-cro-sheet-sizes]', sizeSheet || document);
  if (sheetSizes && radios.length) {
    sheetSizes.style.gridTemplateColumns = $('[data-cro-sizes]', root)?.style.gridTemplateColumns || '';
    radios.forEach((r) => {
      const label = r.closest('.cro-size').cloneNode(true);
      const input = label.querySelector('input');
      input.name = 'cro-sheet-size';
      input.checked = false;
      sheetSizes.appendChild(label);
    });
    sheetSizes.addEventListener('change', (e) => {
      const v = e.target.value;
      const main = radios.find((r) => r.value === v);
      if (!main) return;
      main.checked = true;
      onSize(false);
      closeSheet(sizeSheet);
      if (isInCart(v)) inCartToast(main.dataset.title);
      else add(v, 'size_sheet');
      // the sheet's own history entry is popped on close; write ?variant= after that lands
      setTimeout(() => setVariantUrl(v), 400);
    });
    sizeSheet.addEventListener('cro:close', () => $$('input', sheetSizes).forEach((i) => (i.checked = false)));
  }

  /* ---------------- add to cart */
  const setState = (state, text) => {
    atc.classList.toggle('is-busy', state === 'busy');
    atc.classList.toggle('is-done', state === 'done');
    if (text) atcLabel.textContent = text;
  };
  async function add(variantId, via) {
    if (busy) return;
    busy = true;
    const before = atcLabel.textContent;
    setState('busy', 'Adding…');
    const v = (data.variants || []).find((x) => String(x.id) === String(variantId));
    const preview = {
      title: data.title,
      handle: data.handle,
      url: location.pathname,
      img: (data.zoom || [])[0] || '',
      size: v && v.title !== 'Default Title' ? v.title : '',
      price: v ? v.price : 0,
    };
    const r = await addToCart(variantId, { openDrawer: true, productId: data.id, preview });
    busy = false;
    if (r.ok) {
      setState('done', 'Added to cart');
      track('cro_pdp_add', { product_id: data.id, variant_id: variantId, via });
      setTimeout(() => {
        setState('idle');
        label();
      }, 1600);
    } else {
      setState('idle', before);
      toast(r.message);
    }
  }
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!data.available) return;
    const hidden = $('input[type=hidden][name=id], select[name=id]', form);
    const r = selected();
    const id = r ? r.value : hidden ? hidden.value : '';
    if (isInCart(id)) {
      openCart();
      track('cro_pdp_view_cart', { product_id: data.id, variant_id: id });
      return;
    }
    if (r) return add(r.value, 'cta');
    if (hidden && hidden.value) return add(hidden.value, 'cta');
    if (sizeSheet && radios.length) {
      openSheet(sizeSheet);
      track('cro_size_prompt', { product_id: data.id });
    } else {
      form.classList.add('is-err');
      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      busy = false;
      setState('idle');
      onSize();
    }
  });

  /* ---------------- top bar */
  $('[data-cro-back]', root)?.addEventListener('click', (e) => {
    let sameSite = false;
    try {
      sameSite = document.referrer && new URL(document.referrer).host === location.host;
    } catch (_) {}
    if (sameSite && history.length > 1) history.back();
    else location.href = e.currentTarget.dataset.fallback || '/collections/all';
  });
  // the photo's icons and the scroll-up bar share the same buttons
  $$('[data-cro-search]', root).forEach((b) => b.addEventListener('click', openSearch));
  $$('[data-cro-cart]', root).forEach((b) => b.addEventListener('click', openCart));
  $$('[data-cro-menu]', root).forEach((b) =>
    b.addEventListener('click', () => {
      openMenu();
      track('cro_pdp_menu', { from: b.closest('[data-cro-sbar]') ? 'bar' : 'photo' });
    })
  );
  $$('[data-cro-share]', root).forEach((b) =>
    b.addEventListener('click', () => share(b.closest('[data-cro-sbar]') ? 'bar' : 'photo'))
  );
  initScrollBar();

  /* ---------------- share: the phone's own share sheet; where that doesn't exist (Instagram's and
     Facebook's Android browsers) our sheet with WhatsApp + copy link. Links carry utm_source=share. */
  const shareSheet = $('[data-cro-share-sheet]', root);
  const shareLink = () => {
    const u = new URL(location.pathname, location.origin);
    u.searchParams.set('utm_source', 'share');
    u.searchParams.set('utm_medium', 'product_page');
    return u.toString();
  };
  const shareText = () => `Check this out on ${data.shop || 'Cahoot'}: ${data.title || document.title}`;
  async function share(from) {
    const url = shareLink();
    track('cro_pdp_share', { from });
    if (navigator.share) {
      try {
        await navigator.share({ title: data.title || document.title, text: shareText(), url });
        track('cro_pdp_shared', { method: 'native' });
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return; // the shopper closed the share sheet
      }
    }
    if (!shareSheet) return;
    $('[data-cro-share-wa]', shareSheet).href = 'https://wa.me/?text=' + encodeURIComponent(`${shareText()} ${url}`);
    openSheet(shareSheet);
  }
  $('[data-cro-share-wa]', shareSheet)?.addEventListener('click', () => {
    track('cro_pdp_shared', { method: 'whatsapp' });
    setTimeout(() => closeSheet(shareSheet), 400);
  });
  $('[data-cro-share-copy]', shareSheet)?.addEventListener('click', async () => {
    const ok = await copyText(shareLink());
    closeSheet(shareSheet);
    toast(ok ? 'Link copied. Paste it anywhere to share.' : 'Could not copy the link');
    if (ok) track('cro_pdp_shared', { method: 'copy' });
  });

  /* ---------------- scroll-up bar: slides in on a deliberate scroll up once the photo's icons are
     off screen; hides on scroll down, while a sheet is open and when the photo's icons are back. */
  function initScrollBar() {
    const bar = $('[data-cro-sbar]', root);
    const top = $('[data-cro-top]', root);
    if (!bar || !top || !('IntersectionObserver' in window)) return;
    let on = false;
    let past = false;
    let lastY = scrollY;
    let run = 0;
    const set = (v) => {
      if (v === on) return;
      on = v;
      bar.classList.toggle('is-on', v);
      bar.inert = !v;
      bar.setAttribute('aria-hidden', v ? 'false' : 'true');
    };
    new IntersectionObserver(([e]) => {
      past = !e.isIntersecting;
      if (!past) set(false);
    }).observe(top);
    addEventListener(
      'scroll',
      () => {
        const y = scrollY;
        const dy = y - lastY;
        lastY = y;
        if (!past || document.documentElement.style.overflow === 'hidden') return;
        if (y <= 0 || y + innerHeight >= document.documentElement.scrollHeight - 2) return; // iOS bounce at the ends
        run = dy < 0 === run < 0 ? run + dy : dy;
        if (run < -12) set(true);
        else if (run > 6) set(false);
      },
      { passive: true }
    );
  }

  /* ---------------- EMI schedule dates */
  $$('[data-cro-emi-m]', root).forEach((el) => {
    const d = new Date();
    d.setMonth(d.getMonth() + Number(el.dataset.croEmiM));
    el.textContent = d.toLocaleString('en-IN', { day: 'numeric', month: 'short' });
  });

  /* ---------------- recommendations: fetch when ~1.5 screens away */
  const recs = $('[data-cro-recs]');
  if (recs && !recs.children.length && 'IntersectionObserver' in window) {
    const sectionId = (recs.closest('.shopify-section')?.id || '').replace('shopify-section-', '');
    const io = new IntersectionObserver(
      async (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        try {
          const doc = await fetchSection(recs.dataset.url, sectionId);
          const fresh = doc.querySelector('[data-cro-recs]');
          if (fresh && fresh.children.length) {
            recs.replaceChildren(...fresh.childNodes);
            refreshWishlist();
          } else recs.remove();
        } catch (e) {
          recs.remove();
        }
      },
      { rootMargin: '0px 0px 1200px 0px' }
    );
    io.observe(recs);
  }

  refreshWishlist();
}
